// PBI 1962 (AB#1968) — flexible service option-group DTOs (see the dedicated
// domain module for the full contract + editor model + wire payload helpers).
import type { ServiceOptionGroupDto } from './serviceOptionGroups';
export type {
    ServiceOptionGroupDto,
    ServiceOptionItemDto,
    OptionSelectionType,
    OptionGroupKind,
} from './serviceOptionGroups';

// S1 (AB#2204) — GET /salon-staff/appointments/{id}: a staff member's OWN booking.
// PII-scoped by the backend: masked phone only, no customer email / id / history.
// Every field except `id` is treated as optional so an older backend degrades
// to the list row instead of breaking the dialog.
export interface StaffAppointmentDetailDto {
    id: number;
    appointmentDate?: string;
    branchLocalStartTime?: string;
    durationMinutes?: number;
    endTime?: string | null;
    status?: number | string;
    cancellationSource?: string | null;
    paymentStatus?: string | null;
    paymentMethod?: string | null;
    paidAt?: string | null;
    serviceName?: string;
    serviceNameAr?: string | null;
    amount?: number | null;
    currencyIso?: string | null;
    staffId?: number | null;
    staffDisplayName?: string | null;
    branchId?: number | null;
    branchName?: string | null;
    branchNameAr?: string | null;
    clientDisplayName?: string | null;
    clientPhoneMasked?: string | null;
    sourceType?: string | null;
    notes?: string | null;
    allowedTransitions?: string[] | null;
    canMarkPaid?: boolean;
}

export interface LogoutResponse {
    success: boolean;
}

// AB#1747 — Per-branch working hours (Calendar v1 prerequisite, C1).
// Sara's BE contract: 7 rows per branch, one per DayOfWeek (0=Sun..6=Sat).
// Closed days carry null OpenTime/CloseTime. Server validates HH:mm format
// and the Open<Close ordering invariant; FE must mirror to fail fast.
export interface BranchHourDto {
    dayOfWeek: number;          // 0=Sun .. 6=Sat
    isOpen: boolean;
    openTime: string | null;    // "HH:mm" or null when isOpen=false
    closeTime: string | null;   // "HH:mm" or null when isOpen=false
    is24Hours: boolean;
}

export interface BranchHoursResponse {
    branchId: number;
    hours: BranchHourDto[];
}

// ──────────────────────────────────────────────────────────────────────────
// Epic AB#1824 — Hours single-editor (Model E). Matrix-shaped GET +
// transactional bulk PUT for the new BusinessDetails > Hours editor.
// Faris brief §3.1 + §3.2: per-branch + per-day rows, server-computed
// Default-row synthesis, optimistic-concurrency via per-cell updatedAt.
//
// `updatedAt` is epoch ms (matches existing FE convention; see categories.ts).
// `lastEditedByUserProfileId` is null for rows seeded by M34 migration.
// `defaultRow.hours[].dayOfWeek` value carries the server's "most-common"
// computation; if branches disagree on a day the row is omitted from
// `defaultRow.hours[]` (the matrix UI infers the chip from per-branch rows).
// ──────────────────────────────────────────────────────────────────────────

/** Per-cell row in the matrix — one (branch, dayOfWeek) hours record. */
export interface EffectiveBranchHourDto {
    dayOfWeek: number;               // 0=Sun .. 6=Sat
    isOpen: boolean;
    openTime: string | null;         // "HH:mm" or null when isOpen=false
    closeTime: string | null;
    is24Hours: boolean;
    /** Epoch ms — sourced from the most recent BranchHoursAudit.OccurredAt. */
    updatedAt: number | null;
    /** Null when last edit was by the M34 migration (system actor). */
    lastEditedByUserProfileId: number | null;
}

/** One branch entry within the matrix shape. */
export interface EffectiveBranchHoursEntryDto {
    branchId: number;
    branchName: string;
    isPrimary: boolean;
    hours: EffectiveBranchHourDto[];     // 7 entries, sorted dayOfWeek 0..6
}

/** Default row — synthesized server-side from per-branch row consensus. */
export interface EffectiveDefaultRowDto {
    /**
     * 0..7 entries, one per DayOfWeek where every selected branch agrees on
     * the same hours value. Days where branches disagree are omitted. The
     * FE renders an "X differs" chip in that case (see Dina §2.3).
     */
    hours: EffectiveBranchHourDto[];
}

/** GET /api/business/hours/effective — full response payload. */
export interface EffectiveBranchHoursResponse {
    businessId: number;
    branches: EffectiveBranchHoursEntryDto[];
    defaultRow: EffectiveDefaultRowDto;
}

/** PUT /api/business/hours/bulk — single day-row inside a branch update. */
export interface SetBusinessHoursDayChange {
    dayOfWeek: number;
    isOpen: boolean;
    openTime: string | null;
    closeTime: string | null;
    is24Hours: boolean;
    /**
     * Optimistic-concurrency token — epoch ms from the GET. Null is treated
     * as "no prior audit row" (post-migration first edit). Mismatch with the
     * server's most-recent audit triggers a 409 Hours.Stale envelope.
     */
    expectedUpdatedAt: number | null;
}

/** PUT /api/business/hours/bulk — one branch's update payload. */
export interface SetBusinessHoursBranchUpdate {
    branchId: number;
    days: SetBusinessHoursDayChange[];
}

/** PUT /api/business/hours/bulk — full request body. */
export interface SetBusinessHoursBulkRequest {
    branches: SetBusinessHoursBranchUpdate[];
}

/** PUT /api/business/hours/bulk — 200 OK response. */
export interface SetBusinessHoursBulkResponse {
    businessId: number;
    branches: Array<{
        branchId: number;
        branchName: string;
        hours: EffectiveBranchHourDto[];
    }>;
    /** Epoch ms — bulk-write commit time. */
    updatedAt: number;
}

/** 409 Hours.Stale rejection — per-cell conflicts the server detected. */
export interface HoursStaleConflictCell {
    branchId: number;
    dayOfWeek: number;
    serverUpdatedAt: string;          // ISO date-time string
}

/** 409 Hours.Stale envelope — see Faris brief §3.2 + Dina §2.7. */
export interface HoursStaleErrorDetails {
    conflictedCells: HoursStaleConflictCell[];
    /** Optional — surfaced by the banner when the BE includes it. */
    lastEditedBy?: string;
    lastEditedAt?: string;
}

// Owner appointments list (salon-owner/appointments GET with query params)
export interface OwnerAppointmentListParams {
    startDate?: string;
    endDate?: string;
    branchId?: number;
    paymentStatus?: number | string;
    categoryId?: number;
    page?: number;
    pageSize?: number;
}

export interface OwnerAppointmentListResponse {
    appointments: Array<{
        id: number;
        serviceName: string;
        customerName: string;
        phoneNumber: string;
        appointmentDate: string;
        duration: string;
        amount: number;
        servicePrice?: number;
        paymentStatusString: string;
        statusString?: string;
        [key: string]: unknown;
    }>;
    totalCount?: number;
}

// Service types for salon staff (read-only view)
export interface ServicePricingOptionDto {
    id: number;
    name: string;
    price: number;
    currency: string;
    duration: string;
}

/**
 * AB#1847 (PBI-I) / AB#1838 — Age axis of the gender matrix.
 *
 * BE serializes as a JSON STRING per Faris F-Q2=A: `"Adult" | "Kid" | "Both"`.
 * Default value is `"Adult"`. The owner is forced to pick a value on the Add
 * / Edit Service modal (no implicit default in the UI).
 *
 * Wiki §3 canonical AR labels — Q1 decision (founder Alaa, 2026-05-31):
 *   - Adult → بالغين
 *   - Kid   → أطفال
 *   - Both  → كلاهما  (NOT "الجميع" — that string is reserved for the gender
 *     axis Everyone label to avoid bilingual overload)
 */
export type AgeGroup = 'Adult' | 'Kid' | 'Both';

export interface ServiceDetailsResponse {
    id: number;
    branchId: number;
    name: string;
    /** O3 (SO-25) — optional Arabic name + description. */
    nameAr?: string | null;
    descriptionAr?: string | null;
    imageUrl: string;
    minDuration: string;
    maxDuration: string;
    /**
     * AB#1840 (PBI-B) / AB#1838 — BE serializes ServiceType as a JSON STRING
     * per Faris F-Q2=A (decision pass 3, 2026-05-31): `"MenOnly" | "WomenOnly" | "Everyone"`.
     * Legacy numeric form is also tolerated by `normalizeServiceType` in
     * `src/components/GenderGlyph/` for callers that still talk to older endpoints.
     */
    serviceType: number | 'MenOnly' | 'WomenOnly' | 'Everyone';
    /**
     * AB#1847 (PBI-I) / AB#1838 — Age axis of the gender matrix. BE serializes
     * as a JSON STRING per Faris F-Q2=A: `"Adult" | "Kid" | "Both"`.
     *
     * Default is `"Adult"` — services created before the AgeGroup column was
     * added back-fill to Adult, and the value is REQUIRED on the create/update
     * forms going forward. AR vocabulary per wiki §3 / founder Q1 (2026-05-31):
     * Adult = "بالغين", Kid = "أطفال", Both = "كلاهما" (NOT "الجميع" — that
     * label is reserved for the gender-axis "Everyone").
     */
    ageGroup: AgeGroup;
    hasHomeService: boolean;
    description: string;
    isActive: boolean;
    categoryName: string;
    pricingOptions: ServicePricingOptionDto[];
    /**
     * PBI 1962 (AB#1968) — flexible option groups (variants & add-ons).
     * Additive alongside `pricingOptions`. Empty/absent when the backend
     * `service_option_groups` flag is OFF; old readers ignore it, new readers
     * feature-detect on its presence. See `src/types/serviceOptionGroups.ts`.
     */
    optionGroups?: ServiceOptionGroupDto[];
    assignedStaffIds?: number[];
}

export interface ServiceListResponse {
    services: ServiceDetailsResponse[];
    totalCount: number;
}

export interface CreateServiceResponse {
    serviceId: number;
    name: string;
    pricingOptions: Array<{ name: string; price: number; currency: string; duration: string }>;
}

// Promotion types for salon staff (read-only view)
//
// AB#1901 — The BE serializes DiscountType / CouponCodeType as STRING names
// ("Percentage", "Alphanumeric") via the global JsonStringEnumConverter.
// The integer enums below are kept for FE-local form state + outbound POST/PUT
// request bodies (mirror of the BE's IntEnum form on the way back in).
// Read-side normalize via `parseDiscountType` / `parseCouponCodeType` in
// `src/lib/enumParsers.ts`. String-union wire types in `src/lib/wireEnums.ts`.
export enum DiscountType {
    Percentage = 1,
    FixedAmount = 2
}

export enum CouponCodeType {
    Alphanumeric = 1,
    Numeric = 2
}

export type PromotionType = 'DiscountedDates' | 'ServiceLevel' | 'Coupon';

export interface PromotionDto {
    id: number;
    businessId: number;
    branchId?: number;
    branchName?: string;
    promotionType: PromotionType;
    discountValue: number;
    /**
     * AB#1901 — Widened to `number | string` to match runtime. BE emits the
     * string name ("Percentage" / "FixedAmount") via JsonStringEnumConverter.
     * Consumers MUST normalize at the boundary — see `parseDiscountType` in
     * `src/lib/enumParsers.ts`.
     */
    discountType: number | string;
    discountTypeDisplay: string;
    isActive: boolean;
    createdAt: string;
    startDate?: string;
    endDate?: string;
    serviceId?: number;
    serviceName?: string;
    minimumAmount?: number;
    couponName?: string;
    couponCode?: string;
    couponQuantity?: number;
    couponUsedCount?: number;
    expiryDate?: string;
    codeType?: number | string;
    promotionName?: string;
    name: string;
    information: string;
}

export interface PromotionListResponse {
    promotions: PromotionDto[];
    totalCount: number;
}

// Branch types (Owner)
export interface BranchDto {
    id: number;
    name: string;
    /** O3 (SO-24) — optional Arabic branch name. */
    nameAr?: string | null;
    address?: string;
    streetAddress?: string;
    city?: string;
    state?: string;
    country?: string;
    phoneNumber?: string;
    latitude?: number;
    longitude?: number;
    primaryHeadQuarter: boolean;
    active: boolean;
    description: string;
    createdAt: string;
}

export interface BusinessBranchListResponse {
    branches: BranchDto[];
    totalCount: number;
}

/**
 * AB#1870 — category gender-targeting ordinal. Discovery/surfacing hint, NOT
 * booking enforcement (enforcement stays at the service level). Mirrors backend
 * `CategoryTargetGender` and serializes as the int ordinal (same convention as
 * `approvalStatus`). Default = `NoPreference` (column non-null, default 0).
 */
export enum CategoryTargetGender {
    NoPreference = 0,
    Female = 1,
    Male = 2,
}

// Category types (Owner)
export interface Category {
    id: number;
    name: string;
    /**
     * Arabic name — added per AB#1817 (Sara backend handoff). Nullable for
     * backward compatibility; clients should fall back to `name` when null.
     */
    nameAr?: string | null;
    imageUrl: string;
    isActive: boolean;
    /**
     * Kept on the wire for one release (AB#1872 — backend handoff §Compat).
     * Always `2 = Approved` now; the UI no longer renders Pending/Rejected
     * states. Will be dropped in a later cleanup PBI.
     */
    approvalStatus: number;
    /**
     * AB#1823 (M36) — UTC ISO-8601 instant the category was retired, or null
     * when live. `archivedAt != null` drives the SuperAdmin-only "Deactivated"
     * status chip + "Deactivated" tab (AB#1872 rename — the storage column
     * name `ArchivedAt` is unchanged; only user-facing labels were renamed).
     * Always null on the customer-facing list (deactivated rows never reach it).
     */
    archivedAt?: string | null;
    /**
     * AB#1870 — gender-targeting ordinal. Default `NoPreference (0)`. Surfaced
     * as a row pill ("For women"/"For men") when not `NoPreference`. Optional on
     * the wire for backward compatibility; treat absent as `NoPreference`.
     */
    targetGender?: CategoryTargetGender;
}

/**
 * AB#1872 — SuperAdmin admin-list filter modes (collapsed). The page reads the
 * full `All` list once (deactivated included) and derives the `Deactivated` tab
 * client-side off `archivedAt`. Pending/Inactive modes were removed with the
 * approval-axis collapse.
 *   - All: every row, deactivated included (the chip distinguishes).
 *   - Deactivated: ArchivedAt != null only.
 */
export type CategoryAdminListMode = 'All' | 'Deactivated';

/**
 * AB#1871 — pre-deactivate blast-radius counts for the BLOCKING deactivate
 * confirm dialog. Returned by `GET /api/Category/admin/{id}/impact`. Counts are
 * point-in-time at dialog-open. (Renamed from `CategoryArchiveImpact` per the
 * founder Archive→Deactivate terminology change — same route, same shape.)
 */
export interface CategoryDeactivateImpact {
    categoryId: number;
    /** N — distinct salons with an active subscription on this master category. */
    salonsSubscribed: number;
    /** M — non-deleted services under those active subscriptions. */
    servicesCount: number;
    /** K — future, non-terminal appointments on those services. */
    upcomingAppointments: number;
    /**
     * X — subset of N whose ONLY active category is this one; they lose ALL
     * category-browse visibility until they re-categorize.
     */
    singleCategorySalons: number;
}

/**
 * AB#1871 — lazy drill-down pagination envelope. Mirrors backend
 * `CategoryImpactPage<T>`. `take` is clamped server-side to ≤100.
 */
export interface CategoryImpactPage<T> {
    items: T[];
    total: number;
    skip: number;
    take: number;
}

/** AB#1871 — affected-salon drill-down row. */
export interface CategoryImpactSalonRow {
    businessId: number;
    name: string;
    city: string;
    isSingleCategory: boolean;
}

/** AB#1871 — affected-service drill-down row. `fromPrice`/`currency` may be null. */
export interface CategoryImpactServiceRow {
    serviceId: number;
    name: string;
    salonName: string;
    fromPrice?: number | null;
    /** ISO-4217 e.g. "JOD" / "AED". Rendered as `From {fromPrice} {currency}`. */
    currency?: string | null;
}

/**
 * AB#1871 — upcoming-booking drill-down row. PDPL-SAFE: date/time + service +
 * salon + status ONLY. The backend projection carries NO customer identity
 * (no id / name / phone / email) — do not expect or render any customer field.
 */
export interface CategoryImpactBookingRow {
    appointmentDateTimeUtc: string;
    serviceName: string;
    salonName: string;
    status: number;
}

/**
 * AB#1818 — Approval status enum mirroring backend `ApprovalStatus`. Kept for
 * the one-release `approvalStatus`-on-wire compat shim; the UI no longer renders
 * Pending/Rejected (AB#1872 collapsed the approval axis).
 */
export enum CategoryApprovalStatus {
    Pending = 1,
    Approved = 2,
    Rejected = 3,
}

export interface CategoryListResponse {
    categories: Category[];
    totalCount: number;
}

export interface RequestCategoryResponse {
    categoryId: number;
    name: string;
    approvalStatus: number;
}

// Business Staff (Owner)
export interface StaffScheduleDto {
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    isAvailable: boolean;
}

export interface BusinessStaffDto {
    id: number;
    fullName: string;
    email: string;
    phoneNumber: string;
    /**
     * AB#1861 — Widened to `number | 'Male' | 'Female' | null` to match
     * runtime. PBI-B / AB#1840 cemented the global `JsonStringEnumConverter`
     * on 2026-06-01, so every BE enum (including `Gender`) now serializes as
     * a string. Consumers MUST normalize at their boundary; see
     * `toFormFromRow` in `pages/Owner/MyBusiness/sections/ManageStaffSection`
     * for the canonical row→int|null parse. The broader audit is AB#1894.
     */
    gender?: number | 'Male' | 'Female' | null;
    role?: number;
    position?: string;
    isActive: boolean;
    rating: number;
    totalRatings: number;
    schedules: StaffScheduleDto[];
    // CR-2026-05-16-v1 — owner-uploaded headshot (or invitee's own once they
    // replace it via the mobile staff app later). Sourced server-side from
    // User.ProfilePictureUrl. May be a relative `uploads/staff-photos/…`
    // path (resolved via resolveImageUrl) or an absolute provider URL.
    photoUrl?: string | null;
    // Batch B (AB#2176) — staff identity is separate from a login. A staff record can be
    // bookable without portal access; `hasLogin` says whether a login is linked and
    // `invitationPending` whether a portal invitation for this record is outstanding.
    hasLogin?: boolean;
    invitationPending?: boolean;
    // CR-2026-05-16-v2 — branches this staff is enrolled at. Empty array =
    // implicit-all (eligible at any active branch). Populated from
    // BusinessStaffBranches join on the BE.
    branchIds?: number[];
}

/**
 * AB#1716 (CR-2026-05-17b) — single slot in the per-branch staff schedule.
 *
 * Payload for PUT /api/salon-owner/staff/{id}/schedule and the response
 * shape from GET /api/salon-owner/staff/{id}/schedule.
 */
export interface ScheduleSlotPayload {
    /**
     * Target branch. Required on multi-branch businesses (BranchCount >= 2);
     * may be null on single-branch businesses (server auto-fills with the
     * sole branch).
     */
    branchId: number | null;
    /** 0 = Sunday … 6 = Saturday. */
    dayOfWeek: number;
    /** ISO time string e.g. "09:00:00" or "09:00". 24-hour. */
    startTime: string;
    /** ISO time string. Must be strictly after startTime. */
    endTime: string;
    isAvailable: boolean;
}

/** AB#1716 — Response shape from GET /api/salon-owner/staff/{id}/schedule. */
export interface StaffScheduleResponse {
    staffId: number;
    slots: ScheduleSlotPayload[];
}

// AB#1976 (CR-2026-07-04) — Staff absence management (break / leave).
// AbsenceType int mirrors the BE enum: 0 Break, 1 SickLeave, 2 Vacation,
// 3 PersonalTime, 4 Emergency.
export type AbsenceTypeValue = 0 | 1 | 2 | 3 | 4;

export interface StaffAbsenceDto {
    id: number;
    staffId: number;
    branchId: number;
    /** BE serializes the enum as its string name (e.g. "Break", "Vacation"). */
    absenceType: string;
    startDate: string;      // yyyy-MM-dd
    endDate: string;        // yyyy-MM-dd
    startTime: string | null; // HH:mm
    endTime: string | null;   // HH:mm
    reason: string | null;
    isRecurring: boolean;
    recurrencePattern: string | null;
}

export interface StaffAbsenceWarning {
    code: string;
    message: string;
    messageAr: string;
}

/** AB#2016 — a customer booking overlapping the proposed absence. */
export interface AbsenceApptConflictDto {
    appointmentId: number;
    date: string;       // yyyy-MM-dd
    startTime: string;  // HH:mm
    endTime: string;
    customerName: string | null;
    serviceName: string;
}

/** AB#2024 — how a proposed break would exceed the branch break caps. */
export interface AbsencePolicyBreachDto {
    breaksToday: number;
    maxBreaksPerDay: number;
    minutesToday: number;
    maxMinutesPerDay: number;
}

export interface StaffAbsenceResponse {
    /** Null when requiresConfirmation is true (nothing saved yet). */
    absence: StaffAbsenceDto | null;
    warnings: StaffAbsenceWarning[];
    /** AB#2016 — true when the staff has live bookings in the window; absence NOT saved. */
    requiresConfirmation?: boolean;
    conflictingAppointments?: AbsenceApptConflictDto[];
    /** AB#2024 — set when the break exceeds the branch caps and override is allowed. */
    policyBreach?: AbsencePolicyBreachDto | null;
}

/** AB#2024 — per-branch break policy (GET .../branches/{id}/absence-policy). */
export interface BranchAbsencePolicyDto {
    branchId: number;
    maxBreaksPerDay: number;
    maxBreakMinutesPerDay: number;
    maxSingleBreakMinutes: number;
    allowOwnerOverride: boolean;
}

export interface StaffAbsencesResponse {
    staffId: number;
    absences: StaffAbsenceDto[];
}

/** Request body for POST/PUT /salon-owner/staff/{id}/absences. */
export interface StaffAbsencePayload {
    absenceType: AbsenceTypeValue;
    branchId: number | null;
    startDate: string;        // yyyy-MM-dd
    endDate: string;          // yyyy-MM-dd
    startTime?: string | null; // HH:mm:ss (Break/Emergency)
    endTime?: string | null;
    reason?: string | null;
    isRecurring?: boolean;
    recurrencePattern?: string | null;
    /** AB#2016 — resend true after the owner confirms the appointment-conflict warning. */
    force?: boolean;
}

// CR-2026-05-16-v1 — Pending staff invitations surface.
// Shape returned by GET /api/salon-owner/staff/invitations.
export interface PendingInvitationDto {
    id: number;
    fullName: string;
    email: string;
    phoneNumber: string;
    position?: string | null;
    role: number;
    gender?: number | null;
    /** ISO UTC — when the owner first sent the invitation. */
    invitedAt: string;
    /** ISO UTC — 7 days from issuance; refreshes on resend. */
    expiresAt: string;
    /** The acceptance URL — pasteable into WhatsApp/SMS as the email fallback. */
    invitationLink: string;
}

export interface BusinessStaffListResponse {
    staff: BusinessStaffDto[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

// Business (Owner)
//
// AB#1682 (Phase B BE) extended this shape with: bilingual fields,
// CountryId/CurrencyId FK, direct contact channels, multi-type business
// types, holiday closures, settings, and 4 new social columns on Location.
// FE Phase B follow-up consumes them via mapResponseToForm.
export interface BusinessDetailsResponse {
    id: number;
    name: string;
    about: string;
    businessRegistrationNumber: string;
    isAlwaysOpen: boolean;
    isVerified: boolean;
    /** May be a numeric ServiceType enum or its string name — FE adapter
     *  normalises both. */
    serviceType: string | number;

    // ── AB#1682 — Bilingual + direct contact ──────────────────────────────
    nameAR?: string | null;
    aboutAR?: string | null;
    email?: string | null;
    phone?: string | null;
    website?: string | null;

    // ── AB#1682 — Country / Currency (Id + resolved labels) ───────────────
    countryId?: number;
    countryIso2?: string | null;
    countryNameEn?: string | null;
    currencyId?: number;
    currencyIso?: string | null;

    // ── AB#1682 — Multi-type business types (the salon's selection) ───────
    businessTypes?: BusinessTypeMasterDto[];

    // ── AB#1682 — Holiday closures (chronological) ────────────────────────
    holidayClosures?: HolidayClosureDto[];

    // ── AB#1682 — Booking config + display preferences ────────────────────
    settings?: BusinessSettingsDto;

    // ── Brand assets — latest BusinessDocument URL per type ───────────────
    // Set by BusinessDetailsProjection (BE) when the salon has uploaded a
    // logo/cover via /onboarding/documents/upload. Null when no upload
    // exists yet — the FE renders an empty 📷 / 🖼 placeholder.
    logoUrl?: string | null;
    coverUrl?: string | null;

    location: BusinessLocationDto;
    businessHours: BusinessHoursDto[];
    categories: Array<Record<string, unknown>>;
}

/** AB#1682 — One row from the `BusinessTypes` master picker. */
export interface BusinessTypeMasterDto {
    id: number;
    code: string;
    nameEn: string;
    nameAr: string;
    sortOrder: number;
}

/** AB#1682 — A single holiday / one-off closure. */
export interface HolidayClosureDto {
    id: number;
    /** ISO date string `YYYY-MM-DD`. */
    fromDate: string;
    /** ISO date string `YYYY-MM-DD` (equals fromDate for single-day). */
    toDate: string;
    reason: string;
    /** "Manual" or "Seeded". */
    source: string;
}

/** AB#1682 — Booking config + display preferences. */
export interface BusinessSettingsDto {
    pricingDecimalPlaces: number;
    autoConfirmBookings: boolean;
    allowWalkIns: boolean;
    showRatings: boolean;
}

/** Mirror of BE BusinessLocationDto (extended in AB#1682 with social fields). */
export interface BusinessLocationDto {
    country?: string | null;
    city?: string | null;
    state?: string | null;
    streetAddress?: string | null;
    zipCode?: string | null;
    hasHomeService?: boolean;
    latitude?: number | null;
    longitude?: number | null;
    // AB#1682 — Social channels (the legacy Instagram/WhatsApp/Website
    // existed on the entity but were never DTO-exposed).
    instagram?: string | null;
    whatsApp?: string | null;
    website?: string | null;
    facebook?: string | null;
    twitter?: string | null;
    tikTok?: string | null;
    googleMapsUrl?: string | null;
}

/** Mirror of BE BusinessHoursDto. */
export interface BusinessHoursDto {
    dayOfWeek: number;
    isOpen: boolean;
    openTime?: string | null;
    closeTime?: string | null;
    is24Hours: boolean;
}

// Owner Dashboard
export interface RecentPaymentDto {
    id: number;
    appointmentId: number;
    amount: number;
    currency: string;
    status: string;
    method: string;
    date: string;
    customerName?: string;
}

export interface PaymentMethodBreakdown {
    method: string;
    count: number;
    amount: number;
}

export interface DashboardSummaryResponse {
    totalAppointments: number;
    employees: number;
    activeBranches: number;
    totalPaymentRevenue?: number;
    recentPayments?: RecentPaymentDto[];
    paymentMethodBreakdown?: PaymentMethodBreakdown[];
    bookingsRevenue: {
        labels: string[];
        bookings: number[];
        amounts: number[];
        totalRevenue: number;
        reservationRate: number;
    };
    revenueSeries: {
        labels: string[];
        data: number[];
        year: number;
    };
    appointmentStats: {
        labels: string[];
        values: number[];
    };
}

/* ── AB#1753 — Calendar v1 C7 realtime push payload ─────────────────── */

/**
 * Wire shape of the `appointment.changed` SignalR event broadcast by the
 * BE calendar hub (PR #916). Mirrors `Devanza.Core.Interfaces.AppointmentChangedNotification`.
 *
 * The toast renders `"{actorDisplayName} just booked {serviceName} at {startTime}"`
 * and the cache-invalidation refreshes the day-view grid + availability list.
 */
export interface AppointmentChangedNotification {
    businessId: number;
    branchId: number;
    appointmentId: number;
    /** v1 emits "created" only; "updated"/"deleted" reserved for sibling PBIs. */
    action: 'created' | 'updated' | 'deleted';
    actorDisplayName: string;
    serviceName: string;
    /** "HH:mm" branch-local. */
    startTime: string;
    customerName?: string | null;
}

/* ── AB#1754 — Calendar settings (C8 of Calendar v1 Epic AB#1746) ────────
   Mirrors the deployed BE entity shape (NOT the v0 spec — BE diverged during
   implementation). Field names + types are byte-for-byte what
   `GET /api/calendar/settings` returns.
   ──────────────────────────────────────────────────────────────────────── */

/**
 * Numeric AppointmentStatus enum (mirrors `Devanza.Core.Enums.AppointmentStatus`).
 *
 * The BE uses these int values for `StatusKey`; the FE renders the display
 * label from `LabelEn` / `LabelAr` on each settings row rather than mapping
 * back to a string identifier.
 *
 * Locked-status invariant: Booked (Pending = 1) and Completed (= 3) cannot
 * be disabled — server validator rejects the PUT. FE derives `isLocked`
 * client-side from this set.
 */
export enum AppointmentStatusKey {
    Booked = 1,      // server name: Pending — display label "Booked"
    Approved = 2,    // product-facing "Confirmed" (owner-confirmed booking; AB#2164) — wire name stays `Approved`
    Completed = 3,
    Cancelled = 4,
    NoShow = 5,
    Arrived = 6,
    Started = 7,
}

/** Status keys the locked-status invariant pins on (server-enforced). */
export const LOCKED_STATUS_KEYS: ReadonlySet<AppointmentStatusKey> = new Set([
    AppointmentStatusKey.Booked,
    AppointmentStatusKey.Completed,
]);

/**
 * Per-status settings row (one per AppointmentStatusKey, 7 rows total).
 *
 * AB#1901 (W-10) — `statusKey` widened to `AppointmentStatusKey | string` to
 * match the wire shape. BE sends `"Pending"`/`"Approved"`/… (string names);
 * the FE collapses these to the integer AppointmentStatusKey via
 * `parseAppointmentStatusKey` from `src/lib/enumParsers.ts` at the response
 * boundary. After parsing, downstream code can rely on numeric statusKey.
 */
export interface CalendarStatusSettingsDto {
    statusKey: AppointmentStatusKey | string;
    enabled: boolean;
    /** "#RRGGBB" — server validates with regex. */
    colorHex: string;
    labelEn: string;
    labelAr: string;
}

/**
 * Full calendar settings snapshot for the current business.
 * Tenant scope is implicit (resolved from JWT) — no businessId on the wire.
 */
export interface CalendarSettingsResponse {
    overlapAllowed: boolean;
    backdatingAllowed: boolean;
    /** DayOfWeek enum 0..6 (0=Sunday). BE serializes as int via JsonNumberEnumConverter. */
    weekStart: number;
    /** 5 | 10 | 15 | 30 | 60 minutes (server CHECK constraint). */
    timeSlotMinutes: number;
    /** 12 or 24 — integer, NOT string "12h"/"24h". */
    timeFormat: number;
    /** Always 7 rows in the order returned by the BE (Pending..Started). */
    statuses: CalendarStatusSettingsDto[];
}

/**
 * PUT body. The BE has ONE atomic endpoint: this body replaces both the
 * settings row and ALL 7 status rows in a single transaction. There is NO
 * per-status PUT endpoint.
 */
export interface UpdateCalendarSettingsRequest {
    overlapAllowed: boolean;
    backdatingAllowed: boolean;
    weekStart: number;
    timeSlotMinutes: number;
    timeFormat: number;
    statuses: CalendarStatusSettingsDto[];
}

/* ── AB#1749 — Calendar grid (C3 of Calendar v1 Epic AB#1746) ───────── */

/** One of the six appointment lifecycle states the calendar renders. */
export type CalendarStatusKey =
    | 'Booked'
    | 'Arrived'
    | 'Started'
    | 'Completed'
    | 'Cancelled'
    | 'NoShow';

export interface CalendarStatusDto {
    statusKey: CalendarStatusKey;
    enabled: boolean;
    colorHex: string;
    labelEn: string;
    labelAr: string;
    isLocked: boolean;
}

export interface CalendarStaffColumnDto {
    staffId: number;
    displayName: string;
    /** "Male" / "Female" — null when not set. */
    gender?: string | null;
    /** SAS URL when an Azure blob avatar exists; legacy URL otherwise; null when no photo. */
    photoUrl?: string | null;
    /** "HH:mm" — null when the staff is at the branch but off that day. */
    shiftStart?: string | null;
    shiftEnd?: string | null;
}

export interface CalendarAppointmentDto {
    id: number;
    staffId?: number | null;
    /** "HH:mm" in branch-local time. */
    startTime: string;
    endTime: string;
    /**
     * AB#1901 — Widened to `number | string` to match runtime. BE serializes
     * AppointmentStatus as a string ("Pending"/"Approved"/"Completed"/…) via
     * the global JsonStringEnumConverter. The legacy int values 1..7 are
     * accepted on the type for forward/backward compatibility with cached or
     * legacy responses. Consumers MUST boundary-parse via
     * `parseAppointmentStatus` from `src/lib/enumParsers.ts`.
     */
    status: number | string;
    serviceName: string;
    /** Walk-in name or registered customer's display name. */
    customerName?: string | null;
    customerPhone?: string | null;
    /**
     * AB#1849 (PBI-K) / AB#1838 — Walk-in customer gender, surfaced on the
     * Calendar appointment card as a small ♂ / ♀ glyph next to the customer
     * name when present. May be absent on legacy responses or when the
     * appointment was created at an `Everyone` salon (no walk-in gender
     * captured). FE guards with a null-check before rendering the glyph.
     */
    customerGender?: 'Male' | 'Female' | null;
    /**
     * AB#1797 (B1/W2) — Payment status string. Present when the Calendar grid
     * endpoint includes it (may be absent on legacy responses). "Unpaid" |
     * "Paid" | "Refunded". FE guards with a null-check before rendering the badge.
     */
    paymentStatus?: string | null;
    /** AB#1797 — Amount (decimal) — used for mark-paid modal pre-fill on card. */
    amount?: number;
    /** AB#1797 — Currency ISO code e.g. "AED" | "JOD" */
    currencyIso?: string;
    /**
     * AB#2034 — when set, this card is one leg of a customer package bundle. The grid
     * groups consecutive cards in a staff column that share this id into one visual
     * bundle (accent spine + name chip). Null/absent = standalone appointment.
     */
    bundleBookingId?: number | null;
    /** AB#2034 — the bundle's display name; may be absent (FE falls back to a generic label). */
    bundleName?: string | null;
    /** O1 (AB#2199) — local booking date "yyyy-MM-dd". */
    date?: string;
    /**
     * O1 (AB#2199) — status names the owner may move this booking to, computed by the
     * backend from the same lifecycle rules its state machine validates with. The
     * booking detail renders exactly these; absent/empty = no status actions.
     */
    allowedTransitions?: string[];
    /** O1 (AB#2199) — who cancelled: Customer | Salon | SalonDeclined | Expired | Admin. */
    cancellationSource?: string | null;
    /** O1 (AB#2199, SO-07) — true only when the mark-paid endpoint would accept it. */
    canMarkPaid?: boolean;
}

export interface CalendarGridResponse {
    branchId: number;
    /** "yyyy-MM-dd" or ISO date. */
    date: string;
    /** 0=Sun..6=Sat (matches DayOfWeek serialisation). */
    dayOfWeek: number;
    /** True when BranchHours.IsOpen=false for that day. */
    isClosed: boolean;
    /** "HH:mm" — null when isClosed. */
    dayStart?: string | null;
    dayEnd?: string | null;
    /** Display granularity for time-row spacing on the grid. */
    slotGranularityMinutes: number;
    timeFormat: '12h' | '24h';
    allowOverlappingAppointments: boolean;
    staff: CalendarStaffColumnDto[];
    appointments: CalendarAppointmentDto[];
    /** AB#2015 — staff breaks + leave on this day, rendered as non-bookable blocks. */
    absences?: CalendarAbsenceDto[];
    /** Always exactly 6 rows. FE merges per-status colour/label per appointment.status. */
    statuses: CalendarStatusDto[];
}

/** AB#2015 — one staff absence overlaid on the calendar day grid. */
export interface CalendarAbsenceDto {
    id: number;
    staffId: number;
    /** Enum name: "Break" | "SickLeave" | "Vacation" | "PersonalTime" | "Emergency". */
    absenceType: string;
    /** "HH:mm" — null for whole-day leave. */
    startTime?: string | null;
    endTime?: string | null;
    isWholeDay: boolean;
    reason?: string | null;
}

/* ── AB#1751 — Calendar availability + book (C5 of Calendar v1) ──────── */

export interface CalendarAvailabilitySlot {
    staffId: number;
    staffDisplayName: string;
    /** "Male" / "Female" — null when UserProfile.Gender unset. */
    staffGender?: string | null;
    /** "yyyy-MM-dd". */
    date: string;
    /** "HH:mm" branch-local. */
    startTime: string;
    endTime: string;
}

export interface CalendarAvailabilityResponse {
    slots: CalendarAvailabilitySlot[];
    /** True when caller supplied `around` but no slot matched exactly. */
    isClosestMatch: boolean;
}

export interface CalendarAvailabilityParams {
    branchId: number;
    serviceId: number;
    /** "yyyy-MM-dd". */
    from: string;
    /** "HH:mm" (optional). */
    around?: string | null;
    staffId?: number | null;
    /** 1=Male, 2=Female (matches Devanza.Core.Enums.Gender numeric mapping). */
    staffGender?: number | null;
    take?: number;
}

export interface BookAppointmentRequest {
    branchId: number;
    serviceId: number;
    staffId: number;
    /** "yyyy-MM-dd". */
    date: string;
    /** "HH:mm". */
    startTime: string;
    customerName?: string | null;
    customerPhone?: string | null;
    /**
     * AB#1849 (PBI-K) / AB#1838 — Walk-in customer gender. Per Faris F-Q2=A
     * (decision pass 3, 2026-05-31) the BE serializes the enum as a string.
     * REQUIRED when the business's ServiceType is `MenOnly` or `WomenOnly`;
     * OPTIONAL (null) when the salon is `Everyone`. BE rejects with
     * `CUSTOMER_GENDER_REQUIRED` when the salon is restrictive and this is
     * null, or `CUSTOMER_GENDER_MISMATCHES_SALON` when the value contradicts
     * the salon's ServiceType.
     */
    customerGender?: 'Male' | 'Female' | null;
}

export interface BookAppointmentResponse {
    appointmentId: number;
    startTime: string;
    endTime: string;
    /** True when the BE returned the existing appointment (idempotency replay). */
    isReplay: boolean;
}

// ── AB#893 (Epic AB#937) — Prepaid packages ─────────────────────────────
export enum DurationUnit {
    Days = 0,
    Weeks = 1,
    Months = 2,
}

// The BE serializes DurationUnit as the enum NAME ("Days"/"Weeks"/"Months"),
// though PackageRequest sends it numeric. Normalize the wire form to the numeric
// enum so edit-mode pre-fill and display never silently default to Months.
export const durationUnitToNumber = (u: DurationUnit | string): DurationUnit => {
    if (typeof u === 'number') return u;
    switch (u) {
        case 'Days': return DurationUnit.Days;
        case 'Weeks': return DurationUnit.Weeks;
        case 'Months': return DurationUnit.Months;
        default: return DurationUnit.Months;
    }
};

// i18n key for a duration unit, resilient to either wire form.
export const durationUnitI18nKey = (u: DurationUnit | string): string => {
    switch (durationUnitToNumber(u)) {
        case DurationUnit.Days: return 'packages.unit.days';
        case DurationUnit.Weeks: return 'packages.unit.weeks';
        default: return 'packages.unit.months';
    }
};

export interface PackageItemDto {
    businessServiceId: number;
    serviceName?: string;
    quantity: number;
    unitPrice: number;
}

export interface PackageDto {
    id: number;
    businessId: number;
    name: string;
    packagePrice: number;
    regularPrice: number;
    durationValue: number;
    durationUnit: DurationUnit | string; // BE serializes the enum name (e.g. "Months")
    showInMobileApp: boolean;
    showInOnlineBooking: boolean;
    isActive: boolean;
    isTransferable: boolean;
    maxPurchases?: number | null;
    items: PackageItemDto[];
    /** AB#2027 — branches this package is offered at. Empty = all branches. */
    branchIds?: number[];
    /** AB#2029 — lifecycle. */
    isArchived?: boolean;
    salesEndsAt?: string | null;
    /** Derived display status: Active | Scheduled | Paused | Ended | Archived. */
    status?: string;
}

export interface PackageListResponse {
    packages: PackageDto[];
}

export interface PackageRequest {
    name: string;
    packagePrice: number;
    durationValue: number;
    durationUnit: DurationUnit;
    showInMobileApp: boolean;
    showInOnlineBooking: boolean;
    isActive: boolean;
    isTransferable: boolean;
    maxPurchases?: number | null;
    items: { businessServiceId: number; quantity: number; unitPrice: number }[];
    /** AB#2027 — branches to offer at. Empty/omitted = all branches. */
    branchIds?: number[];
    /** AB#2029 — optional scheduled sales-stop date (ISO). Null = no scheduled stop. */
    salesEndsAt?: string | null;
}
